import { useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { AUTOMATIC_SUPPORTED_UNITS } from '@schuettflix/interfaces';
import { AutomaticProductsFormValues } from '../schema/getAutomaticProductsFormSchema.ts';
import { deriveQuantity } from '../utils/deriveQuantity.ts';

export function useQuantityHelpers(methods: UseFormReturn<AutomaticProductsFormValues>) {
    const { getValues, setValue } = methods;

    const recalculateAdditionalServices = useCallback(
        (sourceQty: number, sourceUnit: AUTOMATIC_SUPPORTED_UNITS, payloadInTons: number) => {
            const additionalServices = getValues('additionalServices');

            if (!additionalServices || additionalServices.length === 0) {
                return;
            }

            additionalServices.forEach((service, index) => {
                if (service.salesUnit) {
                    setValue(
                        `additionalServices.${index}.salesQuantity`,
                        deriveQuantity(sourceQty, sourceUnit, service.salesUnit, payloadInTons)
                    );
                }
                if (service.purchaseUnit) {
                    setValue(
                        `additionalServices.${index}.purchaseQuantity`,
                        deriveQuantity(sourceQty, sourceUnit, service.purchaseUnit, payloadInTons)
                    );
                }
            });
        },
        [getValues, setValue]
    );

    const recalculatePurchaseQuantities = useCallback(
        (sourceQty: number, sourceUnit: AUTOMATIC_SUPPORTED_UNITS, payloadInTons: number) => {
            const [purchaseMaterialUnit, purchaseTransportUnit] = getValues([
                'purchaseMaterialUnit',
                'purchaseTransportUnit',
            ]);
            if (purchaseMaterialUnit) {
                setValue(
                    'purchaseMaterialQuantity',
                    deriveQuantity(sourceQty, sourceUnit, purchaseMaterialUnit, payloadInTons)
                );
            }
            if (purchaseTransportUnit) {
                setValue(
                    'purchaseTransportQuantity',
                    deriveQuantity(sourceQty, sourceUnit, purchaseTransportUnit, payloadInTons)
                );
            }
            recalculateAdditionalServices(sourceQty, sourceUnit, payloadInTons);
        },
        [getValues, setValue, recalculateAdditionalServices]
    );

    const recalculateAll = useCallback(
        (salesMaterialQty: number, salesMaterialUnit: AUTOMATIC_SUPPORTED_UNITS, payloadInTons: number) => {
            const salesTransportUnit = getValues('salesTransportUnit');
            if (salesTransportUnit) {
                setValue(
                    'salesTransportQuantity',
                    deriveQuantity(salesMaterialQty, salesMaterialUnit, salesTransportUnit, payloadInTons)
                );
            }
            recalculatePurchaseQuantities(salesMaterialQty, salesMaterialUnit, payloadInTons);
        },
        [getValues, setValue, recalculatePurchaseQuantities]
    );

    return { recalculatePurchaseQuantities, recalculateAll, recalculateAdditionalServices };
}
